import React, { useState, useEffect, useMemo } from 'react';
import { pocket } from '../api';
import RichTextDisplay from './RichTextDisplay';
import ThemeLabelPicker from './ThemeLabelPicker';
import './UnassignedPocketNotesSection.css';

/**
 * UnassignedPocketNotesSection - Display unassigned Pocket recording summaries
 * Coach can assign each to a student
 */
const UnassignedPocketNotesSection = ({ allPlayers = [], userId = null, onAssignmentSuccess = null, refreshTrigger = 0 }) => {
  // Students to assign to, alphabetical (case-insensitive) - copy, never mutate the prop
  const sortedPlayers = useMemo(
    () => [...allPlayers].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [allPlayers]
  );
  const [unassignedNotes, setUnassignedNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assigningNoteId, setAssigningNoteId] = useState(null);
  const [refreshingNoteId, setRefreshingNoteId] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [selectedPlayersForNote, setSelectedPlayersForNote] = useState({});
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  // Collapsed by default - this section now lives at the top of the
  // generic Notes tab, above the actual note-writing UI, so it shouldn't
  // eat screen space until the coach actually wants to look at it.
  const [collapsed, setCollapsed] = useState(true);

  // Force-pull button: Pocket is normally only checked once an hour (see
  // services/pocketPollingJob.js's cron) - this lets the coach pull right
  // now instead of waiting, e.g. right after finishing a lesson.
  const [pulling, setPulling] = useState(false);
  const [pullStatus, setPullStatus] = useState(null); // 'success' | 'error' | null
  const [pullMessage, setPullMessage] = useState('');

  useEffect(() => {
    loadUnassignedNotes();
  }, [userId, refreshTrigger]);

  // Lesson-briefing notification tap for one specific unassigned summary:
  // open the section, expand that summary, scroll to it and flash it.
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    const bump = () => setFocusTick(t => t + 1);
    window.addEventListener('focusPocketNote', bump);
    return () => window.removeEventListener('focusPocketNote', bump);
  }, []);

  useEffect(() => {
    let wanted = null;
    try { wanted = sessionStorage.getItem('focusPocketNote'); } catch (e) { return undefined; }
    if (!wanted || loading) return undefined;

    if (!unassignedNotes.some(n => n.id === wanted)) {
      // Not in the list (already assigned / removed) - the section itself is still shown
      sessionStorage.removeItem('focusPocketNote');
      setCollapsed(false);
      return undefined;
    }

    setCollapsed(false);
    setExpandedNoteId(wanted);
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const el = document.querySelector(`[data-note-id="${wanted}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('note-focus-flash');
        setTimeout(() => el.classList.remove('note-focus-flash'), 3500);
        sessionStorage.removeItem('focusPocketNote');
        clearInterval(timer);
      } else if (tries >= 20) {
        sessionStorage.removeItem('focusPocketNote');
        clearInterval(timer);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [unassignedNotes, loading, focusTick]);

  const loadUnassignedNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await pocket.getUnassignedNotes(userId);
      setUnassignedNotes(response.data.unassignedNotes || []);
    } catch (err) {
      console.error('❌ Error loading unassigned Pocket notes:', err);
      setError('Failed to load unassigned notes');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayerForNote = (noteId, playerId) => {
    setSelectedPlayersForNote(prev => {
      const current = prev[noteId] || [];
      const next = current.includes(playerId)
        ? current.filter(id => id !== playerId)
        : [...current, playerId];
      return { ...prev, [noteId]: next };
    });
  };

  const handleAssignNote = async (noteId) => {
    const playerIds = selectedPlayersForNote[noteId] || [];
    if (playerIds.length === 0) {
      alert('Please select at least one student first');
      return;
    }

    setAssigningNoteId(noteId);
    try {
      await pocket.assignNoteToPlayers(noteId, playerIds);

      // Remove from unassigned list
      setUnassignedNotes(unassignedNotes.filter(note => note.id !== noteId));

      // Clear selection
      const newSelected = { ...selectedPlayersForNote };
      delete newSelected[noteId];
      setSelectedPlayersForNote(newSelected);

      alert(`✅ Note assigned to ${playerIds.length} student${playerIds.length !== 1 ? 's' : ''}!`);

      // Trigger refresh of player's notes
      if (onAssignmentSuccess) {
        onAssignmentSuccess();
      }
    } catch (err) {
      console.error('❌ Error assigning note:', err);
      alert('Failed to assign note: ' + (err.response?.data?.error || err.message));
    } finally {
      setAssigningNoteId(null);
    }
  };

  const handleRefreshNote = async (noteId) => {
    setRefreshingNoteId(noteId);
    setRefreshError(null);
    try {
      const res = await pocket.refreshUnassignedNote(noteId);
      setUnassignedNotes(prev => prev.map(note =>
        note.id === noteId ? { ...note, summary: res.data.summary } : note
      ));
    } catch (err) {
      console.error('❌ Error refreshing note:', err);
      setRefreshError(err.response?.data?.error || 'Failed to refresh from Pocket');
    } finally {
      setRefreshingNoteId(null);
    }
  };

  const handleLabelTheme = async (noteId, themeName) => {
    const res = await pocket.labelUnassignedNoteTheme(noteId, themeName);
    const addedName = res.data.theme.name;
    setUnassignedNotes(prev => prev.map(note => {
      if (note.id !== noteId) return note;
      const current = note.detectedThemes || [];
      return current.includes(addedName)
        ? note
        : { ...note, detectedThemes: [...current, addedName] };
    }));
  };

  const handlePullNow = async () => {
    setPulling(true);
    setPullStatus(null);
    setPullMessage('');
    try {
      const res = await pocket.forcePoll();
      setPullStatus('success');
      setPullMessage(res.data.message || 'Checked Pocket for new recordings');
      // New recordings may have just landed as unassigned summaries
      await loadUnassignedNotes();
      // Titles edited in Pocket are copied onto assigned notes too: reload the students' notes so the Assigned list shows them
      if (res.data.titlesUpdated && onAssignmentSuccess) onAssignmentSuccess();
    } catch (err) {
      console.error('❌ Error force-pulling Pocket recordings:', err);
      setPullStatus('error');
      setPullMessage(err.response?.data?.error || 'Failed to reach Pocket');
    } finally {
      setPulling(false);
      setTimeout(() => {
        setPullStatus(null);
        setPullMessage('');
      }, 6000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Show section even if empty (so users know the feature exists)

  const pullButton = (
    <div className="pocket-pull-row">
      <button
        type="button"
        className="pocket-pull-button"
        onClick={handlePullNow}
        disabled={pulling}
        title="Pocket is checked automatically once an hour - this pulls new recordings right now instead of waiting"
      >
        <span className={pulling ? 'pocket-pull-spin' : ''}>
          {pulling ? '⏳' : pullStatus === 'success' ? '✅' : pullStatus === 'error' ? '❌' : '🔄'}
        </span>
        {pulling ? 'Pulling from Pocket...' : 'Pull from Pocket Now'}
      </button>
      {pullMessage && (
        <span className={`pocket-pull-message ${pullStatus === 'error' ? 'pocket-pull-message--error' : ''}`}>
          {pullMessage}
        </span>
      )}
    </div>
  );

  if (error) {
    return (
      <div className="unassigned-notes-section">
        {pullButton}
        <div className="unassigned-notes-error">
          ❌ {error}
        </div>
      </div>
    );
  }

  return (
    <div className="unassigned-notes-section">
      {pullButton}
      <div
        className="unassigned-notes-header clickable"
        onClick={() => setCollapsed(!collapsed)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
      >
        <div className="unassigned-notes-header-row">
          <span className="expand-icon section-expand-icon">{collapsed ? '▶' : '▼'}</span>
          <h3>📌 Unassigned Pocket Summaries</h3>
        </div>
        <p className="unassigned-notes-count">
          {unassignedNotes.length} recording{unassignedNotes.length !== 1 ? 's' : ''} waiting to be assigned
        </p>
      </div>

      {!collapsed && (loading ? (
        <div className="unassigned-notes-loading">Loading unassigned notes...</div>
      ) : (
        <div className="unassigned-notes-list">
          {unassignedNotes.map(note => {
            const isExpanded = expandedNoteId === note.id;
            return (
              <div key={note.id} data-note-id={note.id} className={`unassigned-note-card ${isExpanded ? 'expanded' : ''}`}>
                <div
                  className="unassigned-note-header clickable"
                  onClick={() => {
                    setExpandedNoteId(isExpanded ? null : note.id);
                    setRefreshError(null);
                  }}
                >
                  <div className="unassigned-note-meta">
                    <div className="unassigned-note-title-row">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      <h4 className="unassigned-note-player-name">
                        👤 {note.playerNameRecorded || 'Unknown Player'}
                      </h4>
                    </div>
                    <p className="unassigned-note-date">
                      📅 {formatDate(note.date)}
                    </p>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className="unassigned-note-content">
                      <div className="unassigned-note-summary">
                        <RichTextDisplay content={note.summary || 'No summary available'} />
                      </div>

                      <div className="unassigned-note-refresh-row">
                        <button
                          type="button"
                          onClick={() => handleRefreshNote(note.id)}
                          className="refresh-summary-button"
                          disabled={refreshingNoteId === note.id}
                          title="Re-fetch the summary from Pocket, in case you edited it there after this was imported"
                        >
                          {refreshingNoteId === note.id ? 'Refreshing...' : '🔄 Refresh from Pocket'}
                        </button>
                        {refreshError && (
                          <span className="refresh-error-text">{refreshError}</span>
                        )}
                      </div>

                      <div className="unassigned-note-themes">
                        <strong>🏷️ Themes:</strong>
                        <div className="theme-tags">
                          {(note.detectedThemes || []).map((theme, idx) => (
                            <span key={idx} className="theme-tag">
                              {theme}
                            </span>
                          ))}
                          <ThemeLabelPicker
                            coachId={userId}
                            existingThemes={note.detectedThemes || []}
                            onLabel={(themeName) => handleLabelTheme(note.id, themeName)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="unassigned-note-assign">
                      <div className="player-multiselect">
                        {sortedPlayers.map(player => {
                          const checked = (selectedPlayersForNote[note.id] || []).includes(player._id);
                          return (
                            <label key={player._id} className="player-multiselect-option">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePlayerForNote(note.id, player._id)}
                                disabled={assigningNoteId === note.id}
                              />
                              {player.name || player.firstName}
                            </label>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handleAssignNote(note.id)}
                        className="assign-button"
                        disabled={assigningNoteId === note.id || (selectedPlayersForNote[note.id] || []).length === 0}
                      >
                        {assigningNoteId === note.id
                          ? 'Assigning...'
                          : (selectedPlayersForNote[note.id] || []).length > 0
                            ? `Assign to ${selectedPlayersForNote[note.id].length}`
                            : 'Assign'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default UnassignedPocketNotesSection;
